import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';

export interface LiveStageMetrics {
  stageId: string;
  stageName: string;
  stageOrder: number;
  activeTasks: number;
  completedTasks: number;
  avgDurationMinutes: number | null;
  slaHours: number | null;
  slaStatus: 'ok' | 'warning' | 'breach';
}

export type LiveMetricsMap = Map<string, LiveStageMetrics>;

async function fetchLiveMetrics(
  templateId: string,
  organizationId: string
): Promise<LiveMetricsMap> {
  const { data: stages, error: stagesError } = await supabase
    .from('workflow_stages_v9')
    .select('id, name, stage_order, sla_hours')
    .eq('template_id', templateId)
    .eq('organization_id', organizationId)
    .eq('is_active', true);

  if (stagesError || !stages?.length) return new Map();

  const stageIds = stages.map((s) => s.id);

  const { data: tasks } = await supabase
    .from('workflow_tasks_v9')
    .select('id, stage_id, status, started_at, completed_at')
    .in('stage_id', stageIds)
    .eq('organization_id', organizationId);

  const map = new Map<string, LiveStageMetrics>();

  for (const stage of stages) {
    const stageTasks = (tasks || []).filter((t) => t.stage_id === stage.id);
    const activeTasks = stageTasks.filter((t) => t.status === 'in_progress').length;
    const completedTasks = stageTasks.filter(
      (t) => t.status === 'completed' || t.status === 'done' || t.status === 'approved'
    ).length;

    const completedDurations = stageTasks
      .filter((t) => t.started_at && t.completed_at)
      .map((t) => (new Date(t.completed_at).getTime() - new Date(t.started_at).getTime()) / (1000 * 60))
      .filter((d) => d > 0);

    const avgDuration =
      completedDurations.length > 0
        ? completedDurations.reduce((a, b) => a + b, 0) / completedDurations.length
        : null;

    const slaMinutes = stage.sla_hours ? stage.sla_hours * 60 : null;
    let slaStatus: 'ok' | 'warning' | 'breach' = 'ok';

    if (avgDuration !== null && slaMinutes !== null) {
      if (avgDuration > slaMinutes) slaStatus = 'breach';
      else if (avgDuration > slaMinutes * 0.8) slaStatus = 'warning';
    } else if (activeTasks > 0 && slaMinutes !== null) {
      // Check current in-progress durations against SLA
      const inProgressDurations = stageTasks
        .filter((t) => t.status === 'in_progress' && t.started_at)
        .map((t) => (Date.now() - new Date(t.started_at).getTime()) / (1000 * 60))
        .filter((d) => d > 0);
      if (inProgressDurations.length > 0) {
        const maxInProgress = Math.max(...inProgressDurations);
        if (maxInProgress > slaMinutes) slaStatus = 'breach';
        else if (maxInProgress > slaMinutes * 0.8) slaStatus = 'warning';
      }
    }

    map.set(stage.name, {
      stageId: stage.id,
      stageName: stage.name,
      stageOrder: stage.stage_order ?? 0,
      activeTasks,
      completedTasks,
      avgDurationMinutes: avgDuration !== null ? Math.round(avgDuration * 10) / 10 : null,
      slaHours: stage.sla_hours ?? null,
      slaStatus,
    });
  }

  return map;
}

export function useWorkflowLiveMetrics(
  templateId: string | null,
  organizationId: string | null
): { metricsMap: LiveMetricsMap; isLoading: boolean } {
  const [metricsMap, setMetricsMap] = useState<LiveMetricsMap>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const cancelledRef = useRef(false);

  const refresh = useCallback(() => {
    if (!templateId || !organizationId || cancelledRef.current) return;
    fetchLiveMetrics(templateId, organizationId).then((map) => {
      if (!cancelledRef.current) setMetricsMap(map);
    });
  }, [templateId, organizationId]);

  useEffect(() => {
    if (!templateId || !organizationId) {
      setMetricsMap(new Map());
      return;
    }

    cancelledRef.current = false;
    setIsLoading(true);

    fetchLiveMetrics(templateId, organizationId).then((map) => {
      if (!cancelledRef.current) {
        setMetricsMap(map);
        setIsLoading(false);
      }
    });

    const channel = supabase
      .channel(`live-map-${templateId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'workflow_tasks_v9' },
        refresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'workflow_instances_v9' },
        refresh
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      cancelledRef.current = true;
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [templateId, organizationId, refresh]);

  return { metricsMap, isLoading };
}
